/**
 * Quote Request Form Component
 * Handles form submission to Flow Webhooks and variant tracking
 * Extends WebhookFormBase for common functionality
 */

// webhook-form-base.js loads immediately before this script, but can fail
// independently (CSP, network abort, stale cached bundle), leaving WebhookFormBase
// undefined — which throws at the `extends` clause below and aborts the page script.
// Fall back to a no-op base whose constructor leaves `this.form` null, so the
// `if (!this.form) return;` guard in the constructor bails cleanly and the native
// form still submits. See SHOPIFY-1SR.
if (typeof WebhookFormBase === 'undefined') {
  window.WebhookFormBase = class {
    constructor() {
      this.form = null;
    }
  };
}

class QuoteRequestForm extends WebhookFormBase {
  constructor() {
    // Call parent constructor with configuration
    super({
      formId: 'quote-request-form',
      submitButtonId: 'QuoteForm-Submit',
      successMessageId: 'QuoteForm-Success',
      errorMessageId: 'QuoteForm-Error',
      errorTextId: 'QuoteForm-ErrorText',
      loadingAnnouncementId: 'QuoteForm-LoadingAnnouncement',
      configKey: 'QUOTE_FORM_CONFIG',
      translationsKey: 'QUOTE_FORM_TRANSLATIONS'
    });

    // Bail if the base class aborted init (form missing, or WebhookFormUtils /
    // FormUIUtils failed to load — the soft-fail path sets this.disabled). Without
    // the this.disabled check, init below proceeds on a half-constructed object.
    if (!this.form || this.disabled) return;

    // localStorage key for tracking submitted quotes
    this.STORAGE_KEY = 'submitted_quote_product_ids';
    this.EXPIRY_DAYS = 30;

    // Set translation defaults for quote form
    if (!this.translations.submitting_announcement) {
      this.translations = {
        submitting_announcement: 'Submitting quote request, please wait...',
        ...this.translations
      };
    }

    // Check if quote was already submitted for this product
    if (this.hasAlreadySubmitted()) {
      this.showAlreadySubmittedMessage();
      return;
    }

    this.init();
  }

  /**
   * Check if a quote was already submitted for this product
   * @returns {boolean} True if already submitted (and not expired)
   */
  hasAlreadySubmitted() {
    const productId = this.form.getAttribute('data-product-id');
    if (!productId) return false;

    try {
      const data = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      const submission = data[productId];

      if (!submission) return false;

      // Check if expired (30 days)
      const expiryMs = this.EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      const isExpired = Date.now() - submission.timestamp > expiryMs;

      if (isExpired) {
        // Clean up expired entry
        delete data[productId];
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
        return false;
      }

      return true;
    } catch (e) {
      console.warn('Error reading localStorage:', e);
      return false;
    }
  }

  /**
   * Mark this product as having a submitted quote
   */
  markAsSubmitted() {
    const productId = this.form.getAttribute('data-product-id');
    if (!productId) return;

    try {
      const data = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      data[productId] = {
        timestamp: Date.now()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // Handle quota exceeded by clearing old entries and retrying
      if (e.name === 'QuotaExceededError') {
        this.clearExpiredEntries();
        try {
          const freshData = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
          freshData[productId] = { timestamp: Date.now() };
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(freshData));
        } catch (retryError) {
          console.warn('localStorage quota exceeded, could not save:', retryError);
        }
      } else {
        console.warn('Error writing to localStorage:', e);
      }
    }
  }

  /**
   * Clear expired entries from localStorage to free up space
   */
  clearExpiredEntries() {
    try {
      const data = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      const expiryMs = this.EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      const now = Date.now();
      let hasChanges = false;

      for (const productId in data) {
        if (now - data[productId].timestamp > expiryMs) {
          delete data[productId];
          hasChanges = true;
        }
      }

      if (hasChanges) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      }
    } catch (e) {
      console.warn('Error clearing expired entries:', e);
    }
  }

  /**
   * Show the already-submitted thank you message and hide the form
   */
  showAlreadySubmittedMessage() {
    const alreadySubmittedEl = document.getElementById('quote-form-already-submitted');
    if (alreadySubmittedEl) {
      alreadySubmittedEl.style.display = 'block';
    }
    // Hide the form
    if (this.form) {
      this.form.style.display = 'none';
    }
  }

  /**
   * Initialize the form
   */
  init() {
    try {
      this.bindEvents();
      this.updateProductData();
    } catch (error) {
      console.error('Error initializing QuoteRequestForm:', error);
    }
  }

  /**
   * Bind event listeners
   * Extends parent bindEvents() to add quote-specific handlers
   */
  bindEvents() {
    // Call parent to bind form submission
    super.bindEvents();

    // Listen for variant changes
    document.addEventListener('change', (event) => {
      if (event.target.name === 'id') {
        this.updateProductData();
      }
    });

    // Listen for custom variant change events
    document.addEventListener('variant:changed', () => {
      this.updateProductData();
    });

    // The v2 selector never reaches the listener above. It deliberately skips
    // publishing optionValueSelectionChange (variant-selector-v2.js:4536) to
    // avoid racing product-info.js, so product-info.js:320 never dispatches
    // `variant:changed` on that path — it emits `v2:variantChange` instead.
    // Without this the form keeps whatever variant it resolved at init while
    // the customer switches variants freely. Bubbles from the selector
    // container, so a document listener catches it.
    document.addEventListener('v2:variantChange', () => {
      this.updateProductData();
    });

    // Last line of defence: re-sync when the modal is opened, whatever the
    // page did beforehand. ModalComponent dispatches a bubbling `toggle` on
    // every open and close (theme.js:853). This is the moment correctness
    // actually matters — the customer is about to read the variant back.
    const modal = this.form.closest('basic-modal');
    if (modal) {
      modal.addEventListener('toggle', () => {
        if (modal.hasAttribute('open')) this.updateProductData();
      });
    }

    // Setup mutation observer for variant changes
    this.setupMutationObserver();
  }

  /**
   * The main product's v2 selector mount, or null on a PDP that doesn't use it.
   * CTS rows mount their own `#v2-variant-selector-rs-cts-*` containers but never
   * carry `data-product-id`, so this stays unambiguous however many of them render.
   */
  getV2Container() {
    return document.querySelector('[id^="v2-variant-selector-"][data-product-id]');
  }

  /**
   * The form whose input[name="id"] Add to Cart would actually submit.
   *
   * Resolved through the v2 selector's own `data-product-form-id`, the way the
   * selector resolves it before writing to that input (variant-selector-v2.js:4476).
   * A PDP carries several other input[name="id"] elements — CTS row forms, the
   * sticky ATC bar, the quick-view section's form — so an unscoped document-wide
   * lookup is correct only by accident of DOM order.
   */
  getProductForm() {
    const formId = this.getV2Container()?.dataset.productFormId;
    const scopedForm = formId ? document.getElementById(formId) : null;
    return scopedForm || document.querySelector('form.main-product-form');
  }

  /**
   * Get the currently selected variant ID
   */
  getSelectedVariantId() {
    const scope = this.getProductForm() || document;

    let input = scope.querySelector('input[name="id"]:checked');
    if (input) return input.value;

    input = scope.querySelector('select[name="id"]');
    if (input) return input.value;

    input = scope.querySelector('input[name="id"]');
    if (input) return input.value;

    return null;
  }

  /**
   * Get variant data from the page
   */
  getVariantData(variantId) {
    try {
      // Richest source when it matches: carries custom.vendor_sku, which the
      // [data-product-json] fallback below also has but the DOM fallbacks
      // don't. Not scoped to `variant-selects` — the restructure PDP renders
      // product-variant-selector-v2.liquid, which emits this blob in a plain
      // div, so a `variant-selects [data-selected-variant]` selector matches
      // nothing there. The id guard below is what makes the broad selector
      // safe: the blob is server-rendered from
      // product.selected_or_first_available_variant and can disagree with the
      // variant the v2 selector actually arms, so it is only trusted when it
      // describes the variant we asked for.
      const selectedVariantElement = document.querySelector('[data-selected-variant]');
      if (selectedVariantElement) {
        const selectedVariant = JSON.parse(selectedVariantElement.textContent);
        if (selectedVariant && selectedVariant.id.toString() === variantId.toString()) {
          // Prioritize custom.vendor_sku metafield over variant.sku
          const vendorSku = selectedVariant.metafields?.custom?.vendor_sku || selectedVariant.sku || '';
          return {
            id: selectedVariant.id,
            title: selectedVariant.title || selectedVariant.name || '',
            sku: vendorSku
          };
        }
      }

      // Fallback: Try to find variant data in the product JSON
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        const product = JSON.parse(productJson.textContent);
        const variant = product.variants.find(v => v.id.toString() === variantId.toString());
        if (variant) {
          // Prioritize custom.vendor_sku metafield over variant.sku
          const vendorSku = variant.metafields?.custom?.vendor_sku || variant.sku || '';
          return {
            id: variant.id,
            title: variant.title || variant.name || '',
            sku: vendorSku
          };
        }
      }

      // Fallback: the v2 selector's own resolved variant. Needed because both
      // sources above come from Liquid, and `product.variants` returns at most
      // 250 variants unpaginated — products past that cap (Spitfire has more)
      // simply are not in either blob, which left the fields blank. Reaching
      // them through the Storefront API is Shopify's own guidance for
      // high-variant products, and the v2 selector already does exactly that:
      // https://shopify.dev/docs/storefronts/themes/product-merchandising/variants/support-high-variant-products
      // It carries `sku` but no metafields, so vendor_sku is unavailable here
      // and the raw variant SKU is used instead.
      const v2Container = this.getV2Container();
      const v2Variant = (v2Container?.v2Instance || v2Container?._v2Instance)?.currentVariant;
      if (v2Variant && v2Variant.id.toString() === variantId.toString()) {
        return {
          id: v2Variant.id,
          title: v2Variant.title || v2Variant.name || '',
          sku: v2Variant.sku || ''
        };
      }

      // Fallback: try to find variant data in select options
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        const option = variantSelect.querySelector(`option[value="${variantId}"]`);
        if (option) {
          return {
            id: variantId,
            title: option.textContent.trim(),
            sku: option.dataset.sku || ''
          };
        }
      }

      // Final fallback: try to find in hidden variant data
      const variantInputs = document.querySelectorAll('input[name="id"]');
      for (const input of variantInputs) {
        if (input.value === variantId) {
          const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
          return {
            id: variantId,
            title: label ? label.textContent.trim() : '',
            sku: input.dataset.sku || ''
          };
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting variant data:', error);
      return null;
    }
  }

  /**
   * Update product data when variant changes
   */
  updateProductData() {
    try {
      // input[name="id"] is the authority for WHICH variant, because it is the
      // one the Add to Cart form would actually submit — quoting a different
      // variant than the customer could buy is the failure this guards against.
      // The [data-selected-variant] blob used to be read first and trusted
      // unconditionally, which is how the form ended up quoting a variant the
      // page never displayed. It is still consulted inside getVariantData(),
      // but only when its id matches the one resolved here.
      const variantId = this.getSelectedVariantId();

      if (!variantId) {
        // Not a no-op. The v2 selector blanks input[name="id"] whenever the
        // selection is incomplete or resolves to no variant
        // (variant-selector-v2.js:3352, :3368, :3408, :3416 -> :4475), and it
        // leaves its own currentVariant pointing at the last variant that did
        // resolve. Keeping the previous values here would show the customer a
        // variant they moved away from and cannot buy — the exact failure this
        // form exists to prevent — so clear instead of returning.
        console.warn('No variant ID found for quote form update');
        this.applyVariantData(null);
        return;
      }

      const variantData = this.getVariantData(variantId);

      if (!variantData) {
        // The id is trustworthy even when nothing on the page can describe it,
        // so keep it and blank the rest rather than leaving another variant's
        // title and SKU on screen.
        console.warn('Could not find variant data');
        this.applyVariantData({ id: variantId, title: '', sku: '' });
        return;
      }

      this.applyVariantData(variantData);
    } catch (error) {
      console.error('Error updating product data:', error);
    }
  }

  /**
   * Write a resolved variant (or the absence of one) to the hidden fields and to
   * the readonly panel the customer reads. Both move together — a stale panel
   * means they confirm one variant and submit another.
   * @param {{id: string|number, title: string, sku: string}|null} variantData
   */
  applyVariantData(variantData) {
    const id = variantData ? variantData.id : '';
    const title = variantData ? variantData.title || '' : '';
    const sku = variantData ? variantData.sku || '' : '';

    const variantIdField = document.getElementById('QuoteForm-VariantId');
    const variantTitleField = document.getElementById('QuoteForm-VariantTitle');
    const skuField = document.getElementById('QuoteForm-SKU');
    const imageUrlField = document.getElementById('QuoteForm-ImageUrl');

    if (variantIdField) variantIdField.value = id;
    if (variantTitleField) variantTitleField.value = title;
    if (skuField) skuField.value = sku;

    // Update image URL from variant image map. Only when the map exists, so a
    // missing map leaves the server-rendered value alone rather than blanking it.
    if (imageUrlField && window.QUOTE_FORM_VARIANT_IMAGES) {
      imageUrlField.value = (id && window.QUOTE_FORM_VARIANT_IMAGES[id]) || '';
    }

    const variantDisplay = document.getElementById('QuoteForm-VariantDisplay');
    const skuDisplay = document.getElementById('QuoteForm-SKUDisplay');
    const variantItem = document.getElementById('QuoteForm-VariantItem');
    const skuItem = document.getElementById('QuoteForm-SKUItem');
    const separator = document.getElementById('QuoteForm-DetailSeparator');

    // 'Default Title' is Shopify's placeholder on single-variant products, so it
    // is never worth showing — the same condition the Liquid uses to render this
    // item hidden in the first place.
    const hasVariant = Boolean(title) && title !== 'Default Title';
    const hasSku = Boolean(sku);

    if (variantDisplay) variantDisplay.textContent = title;
    if (skuDisplay) skuDisplay.textContent = sku;

    // Hide the items themselves, not their .form-field ancestor — that wrapper
    // is the entire product panel, so hiding it blanks the product name too.
    if (variantItem) variantItem.hidden = !hasVariant;
    if (skuItem) skuItem.hidden = !hasSku;
    // The separator only earns its place with something on both sides of it.
    if (separator) separator.hidden = !hasVariant || !hasSku;

    this.setSubmitAvailability(Boolean(id));
  }

  /**
   * Mirror the buy box. With no resolvable variant, Add to Cart is already
   * disabled and reads "Select options" (buy-buttons.liquid:418), so the quote
   * CTA says the same rather than accepting a request with no variant attached.
   * @param {boolean} hasVariant
   */
  setSubmitAvailability(hasVariant) {
    // Never fight the in-flight loading state: setButtonLoading() owns the
    // button while a submit is running, and re-enabling it there would let a
    // variant change behind the modal open the door to a double submit.
    if (!this.submitButton || this.isSubmitting) return;

    const label = this.submitButton.querySelector('.btn__text');
    if (this.defaultSubmitLabel === undefined) {
      this.defaultSubmitLabel = label ? label.textContent : '';
    }

    this.submitButton.disabled = !hasVariant;
    if (!label) return;
    // Falls back to the button's own label rather than an English literal, so a
    // missing FoxTheme still leaves translated copy on screen.
    label.textContent = hasVariant
      ? this.defaultSubmitLabel
      : this.getSelectOptionsLabel() || this.defaultSubmitLabel;
  }

  /**
   * Already translated in all six locales via products.product.select_options
   * (js-variables.liquid:62), so this needs no string of its own.
   */
  getSelectOptionsLabel() {
    return window.FoxTheme?.variantStrings?.selectOptions || '';
  }

  /**
   * Last guard before the payload is built (called by WebhookFormBase). The
   * disabled button covers the normal path, but setButtonLoading(false)
   * re-enables unconditionally after a submit (webhook-form-utils.js:155), so
   * don't rely on the button alone to keep a variant-less quote out of the queue.
   * @returns {boolean}
   */
  validateCustomFields() {
    const variantIdField = document.getElementById('QuoteForm-VariantId');
    if (variantIdField && !variantIdField.value) {
      this.uiUtils.showMessage('error', this.getSelectOptionsLabel() || 'Select options');
      return false;
    }
    return true;
  }

  /**
   * Setup mutation observer for variant changes
   */
  setupMutationObserver() {
    // Watch the [data-selected-variant] element for content changes. Unscoped
    // for the same reason as getVariantData() — the v2 selector's blob is not
    // inside a `variant-selects` element, so the old selector attached this
    // observer to nothing on the restructure PDP.
    const selectedVariantElement = document.querySelector('[data-selected-variant]');
    if (selectedVariantElement) {
      const observer = new MutationObserver(() => {
        this.updateProductData();
      });

      observer.observe(selectedVariantElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    // Fallback: also watch product form for variant ID changes
    const productForm = document.querySelector('product-form, [data-product-form]');
    if (productForm) {
      const formObserver = new MutationObserver(() => {
        const currentVariantId = this.getSelectedVariantId();
        if (currentVariantId && currentVariantId !== this.lastVariantId) {
          this.lastVariantId = currentVariantId;
          this.updateProductData();
        }
      });

      formObserver.observe(productForm, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-variant-id', 'value']
      });
    }
  }

  /**
   * Build webhook payload from form data (implements abstract method from WebhookFormBase)
   * @returns {Object} Webhook payload
   */
  buildPayload() {
    const formData = new FormData(this.form);

    return {
      payload: {
        type: 'quote_request',
        product: {
          id: formData.get('product_id'),
          title: formData.get('product_title'),
          variant_id: formData.get('variant_id'),
          variant_title: formData.get('variant_title'),
          sku: formData.get('sku'),
          url: formData.get('product_url'),
          image_url: formData.get('product_image_url') || ''
        },
        customer: {
          name: formData.get('customer_name'),
          email: formData.get('customer_email'),
          phone: formData.get('customer_phone') || '',
          preferred_contact: formData.get('preferred_contact')
        },
        quote_details: {
          quantity: parseInt(formData.get('quantity'), 10),
          custom_requirements: formData.get('custom_requirements') || ''
        },
        timestamp: new Date().toISOString()
      }
    };
  }

  /**
   * Reset form after successful submission (implements abstract method from WebhookFormBase)
   */
  resetForm() {
    // Mark this product as submitted to prevent resubmission
    this.markAsSubmitted();

    // Hide the form (the base class will show the success message)
    if (this.form) {
      this.form.style.display = 'none';
    }
  }

  /**
   * Get success message (implements abstract method from WebhookFormBase)
   * @returns {string} Success message
   */
  getSuccessMessage() {
    return 'Quote request submitted successfully!';
  }

  /**
   * Get email subject for fallback submission
   * Note: [FORM FALLBACK] prefix is automatically added by showEmailFallback()
   * @returns {string} Email subject line (without fallback prefix)
   */
  getEmailSubject() {
    const productTitle = this.form.querySelector('[name="product_title"]')?.value || 'Product';
    const variantTitle = this.form.querySelector('[name="variant_title"]')?.value;

    let subject = `Quote Request: ${productTitle}`;
    if (variantTitle) {
      subject += ` - ${variantTitle}`;
    }

    return subject;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuoteRequestForm();
  });
} else {
  new QuoteRequestForm();
}
